// hooks/useStepFormHandler.js
import { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

export const useStepFormHandler = ({
  type = "create",
  entity = "item",
  user,
  item,
  createAction,
  updateAction,
  initialStep = 0,
  totalSteps = 3,
  toastSteps = { 0: false, 1: true, 2: true },
  successRedirectLink,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [step, setStep] = useState(initialStep);
  const [entityId, setEntityId] = useState(null);
  const [loading, setLoading] = useState(false);

  const nextStep = async (status, formData, form, option) => {
    setLoading(true);

    const { draftButton = false } = option || {};

    try {
      if (form) {
        const isValid = await form.trigger();
        if (!isValid) {
          // console.log("Form Errors:", form.formState.errors);
          setLoading(false);
          return null;
        }
      }

      const numericKeys = [
        "item_no",
        "category_id",
        "brand_id",
        "vat",
        "alcohol_percentage",
        "parent_id",
        "order",
        "price"
      ];

      let coercedFormData = { ...formData };
      for (const key of numericKeys) {
        if (coercedFormData[key] !== undefined && coercedFormData[key] !== null) {
          if (coercedFormData[key] === "") {
            coercedFormData[key] = null;
          } else {
            const num = Number(coercedFormData[key]);
            if (!isNaN(num)) {
              coercedFormData[key] = num;
            }
          }
        }
      }

      let payload = {
        ...coercedFormData,
        status: type === "update" ? formData?.status : status || "draft",
        client_id: user.client_id,
        restaurant_id: user.restaurant_id,
      };

      let res;

      // ---- UPDATE mode ----
      if (type === "update") {
        const id = item?.id || entityId;
        if (!id) throw new Error(`${entity} ID not found`);

        if (
          (formData.status === "active" ||
            formData.status === "inactive" ||
            formData.status === "archived") &&
          draftButton
        ) {
          toast.error(
            `${entity} is already ${item?.status} and cannot be saved as draft.`
          );
          setLoading(false);
          return null;
        }

        res = await dispatch(updateAction({ id, data: payload }));

        if (updateAction.fulfilled.match(res)) {
          if (toastSteps[step] || draftButton) {
            if (status === "draft") {
              toast.success(`${entity} saved as draft successfully!`);
            } else {
              toast.success(`${entity} updated successfully!`);
            }
          }

          if (!draftButton) {
            setStep((prev) => Math.min(prev + 1, totalSteps - 1));
          }

          if ((step === totalSteps - 1 && successRedirectLink) || draftButton) {
            navigate(successRedirectLink);
          }

          setEntityId(res.payload.id || id);
          setLoading(false);
          return res.payload;
        } else {
          toast.error(res.payload || `Failed to update ${entity}`);
          setLoading(false);
          return null;
        }
      }

      // ---- CREATE mode ----
      if (type !== "update") {
        const finalPayload = {
          ...payload,
          status: "draft",
        };
        // ---- CREATE / UPDATE ----
        if (step === 0) {
          res = entityId
            ? await dispatch(updateAction({ id: entityId, data: finalPayload }))
            : await dispatch(createAction(finalPayload));

          if (
            createAction.fulfilled.match(res) ||
            updateAction.fulfilled.match(res)
          ) {
            if (toastSteps[step] || draftButton) {
              if (res.payload?.status === "draft") {
                toast.success(`${entity} saved as draft successfully!`);
              } else if (entityId) {
                toast.success(`${entity} updated successfully!`);
              } else {
                toast.success(`${entity} created successfully!`);
              }
            }
            if (!entityId && res.payload?.id) {
              setEntityId(res.payload.id);
            }

            if (!draftButton) {
              setStep((prev) => prev + 1);
            }

            if (draftButton && successRedirectLink) {
              navigate(successRedirectLink);
            }

            setLoading(false);
            return res.payload;
          } else {
            toast.error(res.payload || `Failed to save ${entity}`);
            setLoading(false);
            return null;
          }
        }

        // Step 1+ → Update
        const id = entityId;
        if (!id) throw new Error(`${entity} ID not found`);

        res = await dispatch(updateAction({ id, data: payload }));

        if (updateAction.fulfilled.match(res)) {
          if (toastSteps[step] || draftButton) {
            if (status === "draft") {
              toast.success(`${entity} saved as draft successfully!`);
            } else {
              toast.success(`${entity} published successfully!`);
            }
          }

          if (!draftButton) {
            setStep((prev) => Math.min(prev + 1, totalSteps - 1));
          }

          if ((step === totalSteps - 1 && successRedirectLink) || draftButton) {
            navigate(successRedirectLink);
          }

          setLoading(false);
          return res.payload;
        } else {
          toast.error(res.payload || `Failed to update ${entity}`);
          setLoading(false);
          return null;
        }
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || `Something went wrong`);
      setLoading(false);
      return null;
    }
  };

  const prevStep = () => {
    setStep((prev) => Math.max(prev - 1, 0));
  };

  return {
    step,
    setStep,
    nextStep,
    prevStep,
    entityId,
    loading,
  };
};
